import { useState } from 'react';
import { Bot, MousePointerClick, Search, UserRound } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSeoAgentsQuery, type ApiSeoCrawler } from '@/client/api/marketing/seo';
import { EmptyState } from './components/marketing-ui';
import { MarketingPageShell } from './components/MarketingPageShell';
import { TEMPLATE_LABEL } from './SeoHealthPage';
import {
  NEUTRAL_SERIES,
  RangeTabs,
  SITE_COLOR,
  SeoError,
  SeoKpi,
  SeoKpiRow,
  SeoNote,
  SeoPanel,
  SeoTrend,
  TD,
  TH,
  TR,
  fmtNumber,
} from './components/seo-ui';

const RANGES = [
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

const PURPOSE_LABEL: Record<string, string> = {
  train: 'Training data',
  search: 'Answer-engine index',
  user: 'A person asked',
};

function topTemplates(crawler: ApiSeoCrawler) {
  return Object.entries(crawler.templates)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([template, count]) => `${TEMPLATE_LABEL[template] ?? template} ${fmtNumber(count)}`)
    .join(' · ');
}

function CrawlerTable({ title, subtitle, rows }: { title: string; subtitle: string; rows: ApiSeoCrawler[] }) {
  if (rows.length === 0) return null;
  return (
    <SeoPanel title={title} subtitle={subtitle} flush>
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className={TH}>Crawler</th>
            <th className={TH}>Operator</th>
            <th className={TH}>Why it reads us</th>
            <th className={cn(TH, 'text-right')}>Pages read</th>
            <th className={cn(TH, 'text-right')}>Errors</th>
            <th className={TH}>Reads most</th>
            <th className={TH}>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.family} className={TR}>
              <td className={cn(TD, 'font-medium text-gray-900')}>{c.family}</td>
              <td className={cn(TD, 'text-gray-600')}>{c.operator ?? '—'}</td>
              <td className={cn(TD, 'text-gray-600')}>{c.purpose ? PURPOSE_LABEL[c.purpose] : '—'}</td>
              <td className={cn(TD, 'text-right tabular-nums')}>{fmtNumber(c.hits)}</td>
              <td className={cn(TD, 'text-right tabular-nums', c.errors > 0 ? 'font-semibold text-rose-700' : 'text-gray-500')}>
                {fmtNumber(c.errors)}
              </td>
              <td className={cn(TD, 'text-gray-600')}>{topTemplates(c)}</td>
              <td className={cn(TD, 'whitespace-nowrap text-gray-600')}>{c.last_day}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </SeoPanel>
  );
}

function SimpleList({ rows }: { rows: { key: string; value: number }[] }) {
  return (
    <table className="w-full text-sm">
      <tbody>
        {rows.map((r) => (
          <tr key={r.key} className="border-b border-gray-100 last:border-0">
            <td className="max-w-md break-all py-2 pr-3 text-gray-900">{r.key}</td>
            <td className="py-2 text-right tabular-nums text-gray-900">{fmtNumber(r.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * `/admin/seo/agents` — who reads the site on behalf of an AI, and who arrives
 * from one. Crawlers are counted by ff-ui from the user agent they present
 * (unverified); referrals are the `ai` channel of acquisition visits.
 */
export default function SeoAgentsPage() {
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, error } = useSeoAgentsQuery(days);
  const crawlers = data?.crawlers ?? [];
  const ai = crawlers.filter((c) => c.kind === 'ai');
  const search = crawlers.filter((c) => c.kind === 'search');
  const asked = ai.filter((c) => c.purpose === 'user').reduce((sum, c) => sum + c.hits, 0);
  const referrals = data?.referrals;

  return (
    <MarketingPageShell
      title="AI agents"
      description="Which AI crawlers read our pages, how often they hit errors, and how many visitors AI assistants send us."
      crumbs={[{ label: 'SEO', href: '/admin/seo' }, { label: 'AI agents' }]}
      icon={Bot}
      actions={<RangeTabs value={days} options={RANGES} onChange={setDays} />}
    >
      <div className="space-y-4">
        {error && <SeoError>Could not load AI agent activity.</SeoError>}

        <SeoKpiRow>
          <SeoKpi label="Pages read by AI crawlers" value={data?.totals.ai} icon={Bot} loading={isLoading} />
          <SeoKpi
            label="Fetched because a person asked"
            value={data ? asked : undefined}
            icon={UserRound}
            hint="ChatGPT, Claude, Perplexity and similar, live"
            loading={isLoading}
          />
          <SeoKpi
            label="Visits from AI assistants"
            value={referrals?.visits}
            icon={MousePointerClick}
            hint="People who clicked through"
            loading={isLoading}
          />
          <SeoKpi
            label="Pages read by search engines"
            value={data?.totals.search}
            icon={Search}
            hint="For comparison"
            loading={isLoading}
          />
        </SeoKpiRow>

        {!isLoading && crawlers.length === 0 ? (
          <EmptyState label="No crawler visits recorded in this period yet." />
        ) : (
          <>
            <SeoPanel title="Pages read per day" subtitle="AI crawlers against search-engine crawlers">
              <SeoTrend
                data={data?.daily ?? []}
                series={[
                  { key: 'ai', name: 'AI crawlers', color: SITE_COLOR.feelyfeely, kind: 'bar', stack: 'a' },
                  { key: 'search', name: 'Search engines', color: NEUTRAL_SERIES, kind: 'bar', stack: 'a' },
                ]}
              />
            </SeoPanel>
            <CrawlerTable title="AI crawlers" subtitle="Busiest first" rows={ai} />
            <CrawlerTable title="Search engine crawlers" subtitle="For comparison" rows={search} />
          </>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <SeoPanel title="Visits from AI assistants" subtitle="By assistant">
            {(referrals?.sources.length ?? 0) === 0 ? (
              <p className="py-3 text-sm text-gray-600">No visits from an AI assistant in this period.</p>
            ) : (
              <SimpleList rows={(referrals?.sources ?? []).map((s) => ({ key: s.source, value: s.visits }))} />
            )}
          </SeoPanel>
          <SeoPanel title="Pages AI assistants send people to" subtitle="Top 20">
            {(referrals?.pages.length ?? 0) === 0 ? (
              <p className="py-3 text-sm text-gray-600">Nothing yet.</p>
            ) : (
              <SimpleList rows={(referrals?.pages ?? []).map((p) => ({ key: p.path, value: p.visits }))} />
            )}
          </SeoPanel>
        </div>

        <SeoPanel title="Pages crawlers could not find" subtitle="Answered 4xx, most often first">
          {(data?.missing?.length ?? 0) === 0 ? (
            <p className="py-3 text-sm text-gray-600">None recorded in this period.</p>
          ) : (
            <SimpleList rows={(data?.missing ?? []).map((m) => ({ key: m.path, value: m.count }))} />
          )}
        </SeoPanel>

        <SeoNote>
          Crawlers are identified by the user agent they present, which anyone can copy, so read these as claimed
          identities. Visits are counted only when the assistant passes a referrer or tags the link.
        </SeoNote>
      </div>
    </MarketingPageShell>
  );
}
